import { Link } from "react-router-dom";
import { Trophy } from "lucide-react";

// Round order + human labels
const ROUND_ORDER = ["PRELIM", "R64", "R32", "R16", "QF", "SF", "F", "3PO"];
const ROUND_LABELS = {
  PRELIM: "Preliminary Round",
  R64: "Round of 64",
  R32: "Round of 32",
  R16: "Round of 16",
  QF: "Quarter-Finals",
  SF: "Semi-Finals",
  F: "Final",
  "3PO": "3rd Place",
};

const matchWinner = (m) => {
  if (m.home_score == null || m.away_score == null) return null;
  if (m.home_score > m.away_score) return "home";
  if (m.away_score > m.home_score) return "away";
  // Draw — check shootout
  if (m.shootout_home_score != null && m.shootout_away_score != null && m.shootout_home_score !== m.shootout_away_score) {
    return m.shootout_home_score > m.shootout_away_score ? "home" : "away";
  }
  return null;
};

const TeamRow = ({ team, score, shootout, isWinner, isTbd, side, matchId }) => (
  <div
    className={`flex items-center justify-between px-3 py-2 gap-2 ${
      isWinner ? "bg-[#FFC72C]/10 border-l-2 border-[#FFC72C]" : "border-l-2 border-transparent"
    }`}
    data-testid={`bracket-row-${matchId}-${side}`}
  >
    <div className="flex items-center gap-2 min-w-0">
      {team ? (
        <>
          <img
            src={team.logo_url || "https://placehold.co/24x24/09090b/FFF?text=T"}
            alt=""
            className="w-5 h-5 object-contain flex-shrink-0"
          />
          <Link
            to={`/teams/${team.team_id}`}
            className={`text-sm truncate hover:text-[#009E60] ${
              isWinner ? "font-bold text-[#09090b]" : isTbd ? "text-gray-400 italic" : "text-gray-800"
            }`}
          >
            {team.short_name || team.name}
          </Link>
        </>
      ) : (
        <span className="text-sm italic text-gray-400">TBD</span>
      )}
    </div>
    <div className="flex items-center gap-1.5 flex-shrink-0">
      {shootout != null && (
        <span className="text-[10px] text-gray-500 font-mono">({shootout})</span>
      )}
      <span className={`font-heading font-bold text-sm w-5 text-center ${isWinner ? "text-[#09090b]" : "text-gray-500"}`}>
        {score ?? "—"}
      </span>
    </div>
  </div>
);

const BracketMatchCard = ({ match, teams }) => {
  const winner = matchWinner(match);
  const home = teams[match.home_team_id];
  const away = teams[match.away_team_id];
  return (
    <Link
      to={`/matches/${match.match_id}`}
      className="block bg-white border border-gray-200 hover:border-[#FFC72C] hover:shadow-md transition rounded-sm overflow-hidden"
      data-testid={`bracket-match-${match.match_id}`}
    >
      <TeamRow
        team={home}
        score={match.home_score}
        shootout={match.shootout_home_score}
        isWinner={winner === "home"}
        isTbd={!home}
        side="home"
        matchId={match.match_id}
      />
      <div className="border-t border-gray-100" />
      <TeamRow
        team={away}
        score={match.away_score}
        shootout={match.shootout_away_score}
        isWinner={winner === "away"}
        isTbd={!away}
        side="away"
        matchId={match.match_id}
      />
    </Link>
  );
};

const BracketView = ({ matches, teams }) => {
  // Group by round
  const grouped = ROUND_ORDER.reduce((acc, r) => {
    const list = matches.filter((m) => (m.round || "").toUpperCase() === r);
    if (list.length) acc[r] = list;
    return acc;
  }, {});

  // Preliminary sits OUTSIDE the main bracket columns
  const prelimMatches = grouped.PRELIM || [];
  const mainRoundOrder = ROUND_ORDER.filter((r) => r !== "PRELIM");
  const roundsPresent = mainRoundOrder.filter((r) => grouped[r]);
  const unassigned = matches.filter((m) => !m.round || !ROUND_ORDER.includes(m.round.toUpperCase()));

  if (roundsPresent.length === 0 && prelimMatches.length === 0 && unassigned.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg py-16 px-6 text-center" data-testid="bracket-empty">
        <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="font-heading text-lg font-bold text-[#09090b] mb-1">Bracket not yet drawn</h3>
        <p className="text-gray-500 text-sm max-w-md mx-auto">
          Fixtures will appear here once the ABFA publishes the draw.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="bracket-view" className="space-y-8">
      {/* Preliminary Round — sits above the main bracket, not part of it */}
      {prelimMatches.length > 0 && (
        <section data-testid="bracket-prelim-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-sm md:text-base font-bold uppercase text-gray-600 tracking-widest">
                Preliminary Round
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                Qualifying ties — winners advance into the main bracket
              </p>
            </div>
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-widest">
              {prelimMatches.length} {prelimMatches.length === 1 ? "match" : "matches"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {prelimMatches.map((m) => (
              <BracketMatchCard key={m.match_id} match={m} teams={teams} />
            ))}
          </div>
          <div className="mt-6 border-b border-dashed border-gray-300" />
        </section>
      )}

      {/* Main Bracket */}
      {roundsPresent.length > 0 && (
        <section data-testid="bracket-main">
          {prelimMatches.length > 0 && (
            <h3 className="font-heading text-sm md:text-base font-bold uppercase text-[#09090b] tracking-widest mb-4">
              Main Bracket
            </h3>
          )}
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-6 min-w-max items-start">
              {roundsPresent.map((r) => (
                <div
                  key={r}
                  className="flex-shrink-0 w-64 md:w-72"
                  data-testid={`bracket-round-${r}`}
                >
                  <div className="flex items-center justify-between mb-3 px-1">
                    <h3 className="font-heading text-xs md:text-sm font-bold uppercase text-[#09090b] tracking-widest">
                      {ROUND_LABELS[r]}
                    </h3>
                    <span className="text-[10px] text-gray-400 font-semibold">
                      {grouped[r].length}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {grouped[r].map((m) => (
                      <BracketMatchCard key={m.match_id} match={m} teams={teams} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {unassigned.length > 0 && (
        <div className="mt-8 border-t border-gray-200 pt-6" data-testid="bracket-unassigned">
          <h3 className="font-heading text-xs font-bold uppercase text-gray-500 tracking-widest mb-3">
            Unassigned Round ({unassigned.length})
          </h3>
          <p className="text-xs text-gray-400 mb-3 italic">
            Matches without a round tag. Admins can assign them via Admin → Matches.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {unassigned.map((m) => (
              <BracketMatchCard key={m.match_id} match={m} teams={teams} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BracketView;
